"use client";
import Image from "next/image";
import Link from "next/link";

export default function About() {
	return (
		<div className="min-h-full bg-mgt-black">
			{/* Hero Section */}
			<section className="relative px-4 py-16 md:py-24">
				<div className="max-w-6xl mx-auto text-center">
					<h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
						The World&apos;s First
						<span className="block text-mgt-green">
							Geospatial NFT Marketplace
						</span>
					</h1>
					<p className="text-lg md:text-xl text-off-white max-w-3xl mx-auto leading-relaxed">
						Our patent-pending technology combines the power of blockchain with
						cutting-edge geofencing for an innovative three-step process that
						revolutionizes how we interact with digital assets in real-world
						locations.
					</p>
				</div>
			</section>

			{/* How It Works Section */}
			<section className="px-4 py-16 md:py-20">
				<div className="max-w-6xl mx-auto">
					<h2 className="text-3xl md:text-5xl font-bold text-center text-white mb-4">
						How MyGeoTokens Works
					</h2>
					<p className="text-center text-off-white text-lg mb-16">
						It&apos;s as easy as 1 - 2 - 3!
					</p>

					<div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
						{/* Step 1: Fence */}
						<div className="text-center space-y-6">
							<div className="bg-brown rounded-2xl p-8 md:p-12 hover:bg-brown/80 transition-colors duration-300">
								<div className="w-16 h-16 bg-mgt-green rounded-full flex items-center justify-center mx-auto mb-6">
									<span className="text-2xl font-bold text-mgt-black">1</span>
								</div>
								<h3 className="text-2xl font-bold text-mgt-green mb-4">
									Fence
								</h3>
								<p className="text-off-white leading-relaxed">
									Create geofenced boundaries around real-world locations where
									you want to drop your NFTs. Define the exact area where
									collectors can discover your digital treasures.
								</p>
							</div>
						</div>

						{/* Step 2: Drop */}
						<div className="text-center space-y-6">
							<div className="bg-brown rounded-2xl p-8 md:p-12 hover:bg-brown/80 transition-colors duration-300">
								<div className="w-16 h-16 bg-mgt-green rounded-full flex items-center justify-center mx-auto mb-6">
									<span className="text-2xl font-bold text-mgt-black">2</span>
								</div>
								<h3 className="text-2xl font-bold text-mgt-green mb-4">Drop</h3>
								<p className="text-off-white leading-relaxed">
									Deploy your NFT collection to the Solana blockchain and drop
									them within your geofenced area. Set pricing, quantities, and
									special conditions for your location-based drops.
								</p>
							</div>
						</div>

						{/* Step 3: Snag */}
						<div className="text-center space-y-6">
							<div className="bg-brown rounded-2xl p-8 md:p-12 hover:bg-brown/80 transition-colors duration-300">
								<div className="w-16 h-16 bg-mgt-green rounded-full flex items-center justify-center mx-auto mb-6">
									<span className="text-2xl font-bold text-mgt-black">3</span>
								</div>
								<h3 className="text-2xl font-bold text-mgt-green mb-4">Snag</h3>
								<p className="text-off-white leading-relaxed">
									Collectors visit the real-world location, enter the geofenced
									area, and snag available NFTs. Experience the thrill of
									discovering digital treasures in physical spaces.
								</p>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Technology Section */}
			<section className="px-4 py-16 md:py-20 bg-brown/20">
				<div className="max-w-6xl mx-auto">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
						<div className="space-y-6">
							<h2 className="text-3xl md:text-4xl font-bold text-white">
								Patent-Pending Innovation
							</h2>
							<p className="text-off-white text-lg leading-relaxed">
								Our groundbreaking technology represents the next generation of
								marketing and real-world experiences. By combining blockchain
								technology with advanced geofencing capabilities, we&apos;re
								creating entirely new ways for businesses and creators to engage
								with their audiences.
							</p>
							<div className="space-y-4">
								<div className="flex items-start space-x-3">
									<div className="w-2 h-2 rounded-full bg-mgt-green mt-3 flex-shrink-0"></div>
									<p className="text-off-white">
										Advanced geofencing technology for precise location-based
										interactions
									</p>
								</div>
								<div className="flex items-start space-x-3">
									<div className="w-2 h-2 rounded-full bg-mgt-green mt-3 flex-shrink-0"></div>
									<p className="text-off-white">
										Built on Solana blockchain for fast, low-cost transactions
									</p>
								</div>
								<div className="flex items-start space-x-3">
									<div className="w-2 h-2 rounded-full bg-mgt-green mt-3 flex-shrink-0"></div>
									<p className="text-off-white">
										User-friendly interface designed for simplicity and
										innovation
									</p>
								</div>
							</div>
						</div>
						<div className="bg-brown rounded-2xl p-8 text-center">
							<div className="text-6xl mb-6">📍</div>
							<h3 className="text-xl font-bold text-mgt-green mb-4">
								Built on Solana
							</h3>
							<p className="text-off-white">
								Leveraging Solana&apos;s high-performance blockchain for
								seamless, cost-effective NFT transactions and geospatial
								interactions.
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* Vision Section */}
			<section className="px-4 py-16 md:py-20">
				<div className="max-w-4xl mx-auto text-center">
					<h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
						Our Vision
					</h2>
					<p className="text-lg md:text-xl text-off-white leading-relaxed mb-8">
						We&apos;re stepping into the next generation of marketing and
						real-world experiences. Our vision is to bring simplicity,
						innovation, and a sense of adventure for both app users and NFT
						creators alike.
					</p>
					<div className="bg-brown rounded-2xl p-8 md:p-12">
						<h3 className="text-2xl font-bold text-mgt-green mb-6">
							Be a Part of Our Legacy
						</h3>
						<p className="text-off-white leading-relaxed mb-8">
							Join our active community of innovators, creators, and adventurers
							who are shaping the future of geospatial NFTs. Connect with fellow
							MyGeoTokens enthusiasts who share your passion for adventure and
							innovation.
						</p>
						<div className="flex flex-col sm:flex-row gap-4 justify-center">
							<a
								href="https://discord.gg/mygeotokens"
								target="_blank"
								rel="noopener noreferrer"
								className="bg-mgt-green text-mgt-black px-6 py-3 rounded-full font-medium hover:bg-mgt-yellow transition-colors duration-200"
							>
								Join Our Discord
							</a>
							<a
								href="https://gitbook.mygeotokens.com"
								target="_blank"
								rel="noopener noreferrer"
								className="border border-mgt-green text-mgt-green px-6 py-3 rounded-full font-medium hover:bg-mgt-green hover:text-mgt-black transition-colors duration-200"
							>
								Read Our GitBook
							</a>
						</div>
					</div>
				</div>
			</section>

			{/* Partnerships Section */}
			<section className="px-4 py-16 md:py-20 bg-brown/20">
				<div className="max-w-6xl mx-auto text-center">
					<h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
						Looking for Imaginative Partnerships
					</h2>
					<p className="text-lg text-off-white max-w-3xl mx-auto leading-relaxed mb-12">
						Our product stands on a solid foundation of innovation backed by a
						forward-thinking vision. We seek partners who share our enthusiasm
						and commitment to change the world.
					</p>
					<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
						<div className="bg-brown rounded-2xl p-6">
							<div className="text-4xl mb-4"></div>
							<h3 className="text-xl font-bold text-mgt-green mb-3">
								Innovation
							</h3>
							<p className="text-off-white text-sm">
								Partner with us to explore new frontiers in geospatial
								technology and blockchain innovation.
							</p>
						</div>
						<div className="bg-brown rounded-2xl p-6">
							<div className="text-4xl mb-4">👥</div>
							<h3 className="text-xl font-bold text-mgt-green mb-3">
								Collaboration
							</h3>
							<p className="text-off-white text-sm">
								Join forces with our team to create revolutionary experiences
								that bridge digital and physical worlds.
							</p>
						</div>
						<div className="bg-brown rounded-2xl p-6">
							<div className="text-4xl mb-4">🌐</div>
							<h3 className="text-xl font-bold text-mgt-green mb-3">Impact</h3>
							<p className="text-off-white text-sm">
								Help us shape the future of location-based digital experiences
								and transform how people interact with NFTs.
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* Call to Action */}
			<section className="px-4 py-16 md:py-20">
				<div className="max-w-4xl mx-auto text-center">
					<h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
						Ready to Start Your Geospatial Journey?
					</h2>
					<p className="text-lg text-off-white mb-8">
						Join thousands of creators and collectors who are already exploring
						the future of location-based NFTs.
					</p>
					<div className="flex flex-col sm:flex-row gap-4 justify-center">
						<Link
							href="/signup"
							className="bg-mgt-green text-mgt-black px-8 py-4 rounded-full font-medium hover:bg-mgt-yellow transition-colors duration-200 text-lg"
						>
							Get Started Today
						</Link>
						<Link
							href="/faqs"
							className="border border-mgt-green text-mgt-green px-8 py-4 rounded-full font-medium hover:bg-mgt-green hover:text-mgt-black transition-colors duration-200 text-lg"
						>
							Learn More
						</Link>
					</div>
				</div>
			</section>

			{/* Footer spacing */}
			<div className="h-8"></div>
		</div>
	);
}
